import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import {
  GetSOSchedulesListResponseType,
  GetSOScheduleDestinationsResponseType,
  GetSOSchedulesOriginLocationsResponseType,
  GetSupplyOrderRemarksResponseType,
} from '../../services/soSchedules'
import {
  SLICE_NAME,
  fetchDeleteSOScheduleById,
  fetchDownloadSOSchedulesCsv,
  fetchDownloadSOScheduleDestinationsCsv,
  fetchGetSOSchedules,
  fetchGetSOScheduleDestinations,
  fetchGetSOSchedulesOriginLocations,
  fetchGetSupplyOrderRemarks,
  fetchRunSOScheduleNow,
} from './thunk'

export type SOSchedulesSliceType = {
  isLoading: boolean
  data: GetSOSchedulesListResponseType['data']
  constants: {
    remarks: GetSupplyOrderRemarksResponseType['data']
    locations: GetSOSchedulesOriginLocationsResponseType['data']
  }
  popup: {
    destination: {
      isOpen: boolean
      item: Nullable<GetSOSchedulesListResponseType['data'][number]>
      destinations: GetSOScheduleDestinationsResponseType['data']
      isLoadingDestinations: boolean
    }
    download: {
      isOpen: boolean
    }
  }
}

const initialState: SOSchedulesSliceType = {
  isLoading: false,
  data: [],
  constants: {
    remarks: [],
    locations: [],
  },
  popup: {
    destination: {
      isOpen: false,
      item: null,
      destinations: [],
      isLoadingDestinations: false,
    },
    download: {
      isOpen: false,
    },
  },
}

const soSchedulesSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setPopupDestination: (
      state,
      action: PayloadAction<{
        isOpen: boolean
        item?: GetSOSchedulesListResponseType['data'][number]
      }>,
    ) => {
      state.popup.destination.isOpen = action.payload.isOpen
      state.popup.destination.item = action.payload.item || null
      if (!action.payload.isOpen) {
        state.popup.destination.destinations = []
      }
    },
    setPopupDownload: (state, action: PayloadAction<{ isOpen: boolean }>) => {
      state.popup.download.isOpen = action.payload.isOpen
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<SOSchedulesSliceType>) => {
      builder.addCase(fetchGetSOSchedules.fulfilled, (state, { payload }) => {
        state.data = payload.data
      })
      builder.addCase(fetchGetSOSchedulesOriginLocations.fulfilled, (state, { payload }) => {
        state.constants.locations = payload.data
      })
      builder.addCase(fetchGetSupplyOrderRemarks.fulfilled, (state, { payload }) => {
        state.constants.remarks = payload.data
      })
      builder.addCase(fetchGetSOScheduleDestinations.pending, (state) => {
        state.popup.destination.isLoadingDestinations = true
      })
      builder.addCase(fetchGetSOScheduleDestinations.fulfilled, (state, { payload }) => {
        state.popup.destination.destinations = payload.data
        state.popup.destination.isLoadingDestinations = false
      })
      builder.addCase(fetchGetSOScheduleDestinations.rejected, (state) => {
        state.popup.destination.isLoadingDestinations = false
      })
    },
    [
      fetchGetSOSchedules,
      fetchDeleteSOScheduleById,
      fetchDownloadSOSchedulesCsv,
      fetchDownloadSOScheduleDestinationsCsv,
      fetchGetSOScheduleDestinations,
      fetchRunSOScheduleNow,
    ],
  ),
})

export const { reset, setPopupDestination, setPopupDownload } = soSchedulesSlice.actions

export default soSchedulesSlice.reducer
